import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { ReportsData } from "@/hooks/useReportsData";
import { Flame, Target, Brain } from "lucide-react";

const FlashcardsIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 256 256"
    className={className}
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="currentColor"
      d="M180,72H36A20,20,0,0,0,16,92V204a20,20,0,0,0,20,20H180a20,20,0,0,0,20-20V92A20,20,0,0,0,180,72Zm-4,128H40V96H176ZM240,52V176a12,12,0,0,1-24,0V56H64a12,12,0,0,1,0-24H220A20,20,0,0,1,240,52Z"
    />
  </svg>
);

interface OverviewStatsProps {
  data: ReportsData;
}

export const OverviewStats: React.FC<OverviewStatsProps> = ({ data }) => {
  const totalMastered = data.flashcardDecks.reduce(
    (sum, deck) => sum + deck.mastered_count,
    0
  );
  const totalCards = data.flashcardDecks.reduce(
    (sum, deck) => sum + deck.total_cards,
    0
  );
  const completedQuizzes = data.quizAttempts.filter(
    (a) => a.completed_at !== null
  ).length;

  // Calculate overall accuracy across quizzes and exams
  const totalCorrect =
    data.quizAttempts.reduce((sum, a) => sum + (a.total_correct || 0), 0) +
    data.examAttempts.reduce((sum, a) => sum + (a.total_correct || 0), 0);
  const totalQuestions =
    data.quizAttempts.reduce((sum, a) => sum + a.total_questions, 0) +
    data.examAttempts.reduce((sum, a) => sum + a.total_questions, 0);
  const overallAccuracy =
    totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;

  const stats = [
    {
      label: "Current Streak",
      value: data.profile?.current_streak || 0,
      suffix: "days",
      icon: Flame,
      color: "text-orange",
      bgColor: "bg-orange/10",
    },
    {
      label: "Weekly Sessions",
      value: data.weeklyStudySessions,
      suffix: "sessions",
      icon: Target,
      color: "text-info",
      bgColor: "bg-info/10",
    },
    {
      label: "Overall Accuracy",
      value: overallAccuracy,
      suffix: "%",
      icon: Brain,
      color: "text-correct",
      bgColor: "bg-correct/10",
    },
    {
      label: "Cards Mastered",
      value: totalMastered,
      suffix: `/ ${totalCards}`,
      icon: FlashcardsIcon,
      color: "text-premium",
      bgColor: "bg-premium/10",
    },
  ];

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {stats.map((stat) => (
        <Card key={stat.label} className="border-border bg-card">
          <CardContent className="flex items-center gap-4 p-6">
            <div className={`rounded-xl p-3 ${stat.bgColor}`}>
              <stat.icon className={`h-6 w-6 ${stat.color}`} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{stat.label}</p>
              <p className="text-2xl font-semibold text-foreground">
                {stat.value}
                <span className="ml-1 text-sm font-normal text-muted-foreground">
                  {stat.suffix}
                </span>
              </p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
